import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FolderSimpleIcon as FolderSimple, UploadSimpleIcon as UploadSimple, DownloadSimpleIcon as DownloadSimple, TrashIcon as Trash, FilePdfIcon as FilePdf, FileDocIcon as FileDoc, FileXlsIcon as FileXls, FilePptIcon as FilePpt, ImageIcon as ImageIcon, FileIcon as FileIcon, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";

/**
 * `/admin/documents` — Admin-only business document vault.
 *
 * Houses contracts, financials, legal filings, IP docs, etc. Files
 * stream up to the Emergent objstore; metadata sits in
 * `business_documents`. Categories drive the left-rail filter; the
 * search bar greps title + notes + filename.
 *
 * NOT for user-facing files — those live in `dog_photos` / `litter
 * photos` / pedigree certificates / etc. This page is the founder's
 * personal cabinet.
 */
const CATEGORY_LABELS = {
  legal: "Legal",
  contracts: "Contracts",
  financial: "Financial",
  ip: "Intellectual property",
  compliance: "Compliance",
  marketing: "Marketing",
  operations: "Operations",
  other: "Other",
};

function fileIcon(filename, contentType) {
  const ext = (filename || "").toLowerCase().split(".").pop();
  if (ext === "pdf" || (contentType || "").includes("pdf")) return FilePdf;
  if (["doc", "docx", "rtf", "txt", "md"].includes(ext)) return FileDoc;
  if (["xls", "xlsx", "csv"].includes(ext)) return FileXls;
  if (["ppt", "pptx"].includes(ext)) return FilePpt;
  if (["png", "jpg", "jpeg", "webp", "gif", "heic"].includes(ext)) return ImageIcon;
  return FileIcon;
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  const k = 1024;
  if (bytes < k) return `${bytes} B`;
  if (bytes < k * k) return `${(bytes / k).toFixed(1)} KB`;
  return `${(bytes / k / k).toFixed(1)} MB`;
}

export default function AdminDocuments() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "legal",
    notes: "",
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set("category", filterCat);
      if (q) params.set("q", q);
      const res = await api.get(`/admin/documents?${params.toString()}`);
      setDocs(res.data.documents || []);
      setCategories(res.data.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCat]);

  function onFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setForm((p) => ({ ...p, file: f, title: p.title || f.name }));
  }

  async function submitUpload(e) {
    e.preventDefault();
    if (!form.file) {
      toast.error("Choose a file first");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Add a title");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("notes", form.notes);
      await api.post("/admin/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setForm({ title: "", category: "legal", notes: "", file: null });
      setUploadOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function downloadDoc(doc) {
    try {
      const res = await api.get(`/admin/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `${doc.title}.bin`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  }

  async function deleteDoc(id) {
    if (!window.confirm("Delete this document? Metadata is removed; the underlying file remains in storage.")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      toast.success("Document deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  const cats = categories.length
    ? categories
    : ["legal", "contracts", "financial", "ip", "compliance", "marketing", "operations", "other"];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-documents-page">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-ink flex items-center gap-3">
              <FolderSimple size={32} weight="duotone" className="text-gold-dark" />
              Business documents
            </h1>
            <p className="text-[#6B6B6B] mt-1 text-sm max-w-2xl">
              Admin-only vault for contracts, financials, IP filings, and other business records.
            </p>
          </div>
          <Button
            onClick={() => setUploadOpen((v) => !v)}
            className="rounded-full bg-ink text-white hover:bg-crimson"
            data-testid="docs-upload-toggle"
          >
            <UploadSimple size={16} className="mr-1.5" weight="bold" />
            {uploadOpen ? "Close" : "Upload document"}
          </Button>
        </div>

        {/* Upload form */}
        {uploadOpen && (
          <form
            onSubmit={submitUpload}
            className="rounded-2xl border border-[#E8E0CC] bg-white p-5 grid lg:grid-cols-2 gap-4"
            data-testid="docs-upload-form"
          >
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Master Services Agreement — ACME LLC"
                data-testid="docs-title-input"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="docs-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cats.map((c) => (
                    <SelectItem key={c} value={c}>
                      {CATEGORY_LABELS[c] || c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">File</Label>
              <Input
                type="file"
                onChange={onFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.md,.csv,.png,.jpg,.jpeg,.webp,.gif,.heic,.zip,.tar,.gz"
                data-testid="docs-file-input"
              />
              {form.file && (
                <p className="text-[11px] text-[#6B6B6B] mt-1">
                  {form.file.name} · {fmtSize(form.file.size)}
                </p>
              )}
            </div>
            <div className="lg:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Counterparty, effective date, key terms — anything you'd want when finding this in 2 years."
                className="resize-none"
                data-testid="docs-notes-input"
              />
            </div>
            <div className="lg:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-full bg-gold-gradient text-ink hover:opacity-95 border-0"
                disabled={uploading}
                data-testid="docs-upload-submit"
              >
                {uploading ? "Uploading…" : "Save document"}
              </Button>
            </div>
          </form>
        )}

        {/* Filter bar */}
        <div className="flex items-center gap-3 flex-wrap" data-testid="docs-filter-bar">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search title, notes, or filename…"
              className="pl-9"
              data-testid="docs-search-input"
            />
          </div>
          <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[200px]" data-testid="docs-filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => (
                <SelectItem key={c} value={c}>
                  {CATEGORY_LABELS[c] || c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-full">
            Search
          </Button>
        </div>

        {/* Documents list */}
        <section className="rounded-2xl border border-[#E8E0CC] bg-white" data-testid="docs-list">
          {loading ? (
            <div className="px-5 py-10 text-center text-sm text-[#6B6B6B]">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-[#6B6B6B]">
              No documents yet. Upload your first business record to get started.
            </div>
          ) : (
            <ul className="divide-y divide-[#F0E8D0]">
              {docs.map((d) => {
                const Icon = fileIcon(d.filename, d.content_type);
                return (
                  <li
                    key={d.id}
                    className="px-5 py-4 flex items-center justify-between gap-3 hover:bg-[#FAF7EE]/40"
                    data-testid={`doc-row-${d.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Icon size={28} weight="duotone" className="text-gold-dark shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[14.5px] font-medium text-ink truncate">{d.title}</div>
                        <div className="text-[11.5px] text-[#6B6B6B] mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center bg-[#FBF7EC] text-gold-dark border border-gold/30 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider">
                            {CATEGORY_LABELS[d.category] || d.category}
                          </span>
                          <span className="truncate">{d.filename}</span>
                          <span>·</span>
                          <span>{fmtSize(d.size_bytes)}</span>
                          <span>·</span>
                          <span>{(d.uploaded_at || "").slice(0, 10)}</span>
                        </div>
                        {d.notes && (
                          <div className="text-[12px] text-[#3F3F3F] mt-1 line-clamp-2">{d.notes}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadDoc(d)}
                        className="text-ink hover:text-gold-dark p-2 rounded-md hover:bg-[#FBF7EC]"
                        aria-label="Download"
                        data-testid={`doc-download-${d.id}`}
                      >
                        <DownloadSimple size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteDoc(d.id)}
                        className="text-[#9C7E25] hover:text-crimson p-2 rounded-md hover:bg-[#FEF5F5]"
                        aria-label="Delete"
                        data-testid={`doc-delete-${d.id}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
